import { Card, Col, Row, Space, Tag, Tooltip, Typography } from 'antd';
import { Segmented } from 'antd/lib';
import {
  Page,
  StrykerPerformanceIndicatorColor,
  StrykerPrimaryColor,
} from 'osep-react-antd';
import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import packageJson from '../../package.json';
import InlineMarkdown from '../components/InlineMarkdown';
import useCli from '../hooks/query/useCli';
import findImageByProjectNameOrType from '../utils/findImageByProjectNameOrType';
import ErrorPage from './ErrorPage';

const SectionFilters: React.FC<{
  sections: string[];
  onChange: (value: string) => void;
}> = ({ sections, onChange }) => {
  const [selectedSection, setSelectedSection] = React.useState<string>(
    sections[0],
  );

  const handleSectionChange = (value: string) => {
    setSelectedSection(value);
    onChange(value);
  };

  return (
    <Segmented<string>
      value={selectedSection}
      options={sections}
      onChange={handleSectionChange}
      size="large"
      block
    />
  );
};

const AddProjectsPage: React.FC = () => {
  const { data, isLoading, isError, error } = useCli();
  const [selectedSection, setSelectedSection] = React.useState<string>();
  const showShortLabels = useMediaQuery({ maxWidth: 600 });

  const navigate = useNavigate();

  if (isError) {
    return <ErrorPage error={error} />;
  }

  const sortedCommands = data
    ? (data.commands?.find((cmd) => cmd.name === 'create')?.commands || [])
        .filter((cmd) => !cmd.osep_generator_ui_is_hidden)
        .sort((a, b) => a.name.localeCompare(b.name))
    : undefined;

  const filteredCommands = selectedSection
    ? sortedCommands?.filter(
        (cmd) =>
          cmd.osep_generator_ui_project_sections?.some((section) =>
            showShortLabels
              ? section.short_label === selectedSection
              : section.label === selectedSection,
          ),
      )
    : sortedCommands;

  const allSections = Array.from(
    new Map(
      (sortedCommands ?? [])
        .flatMap((cmd) => cmd.osep_generator_ui_project_sections ?? [])
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((section) => [section.label, section]),
    ).values(),
  );

  return (
    <Page
      title="Add Project"
      onBack={() => navigate('/projects')}
      isLoading={isLoading}
      footer={<Typography.Text disabled>{packageJson.version}</Typography.Text>}
    >
      <Space
        direction="vertical"
        style={{ display: 'flex', paddingBottom: 24 }}
      >
        <Card>
          <Card.Meta
            description={
              <SectionFilters
                sections={allSections.map((section) =>
                  showShortLabels ? section.short_label : section.label,
                )}
                onChange={setSelectedSection}
              />
            }
          ></Card.Meta>
        </Card>
      </Space>
      <Row gutter={[16, 16]}>
        {(filteredCommands ?? []).map((command) => (
          <Col key={command.name} xs={24} sm={24} md={12} lg={8} xl={6}>
            <Card
              title={command.osep_generator_ui_project_name}
              hoverable
              style={{
                height: '100%',
                position: 'relative',
              }}
              onClick={() => navigate(`/projects/add/${command.name}`)}
              cover={
                <Space style={{ textAlign: 'center' }}>
                  <img
                    src={findImageByProjectNameOrType(
                      command.osep_generator_ui_project_group,
                    )}
                    alt={`${command.osep_generator_ui_project_group} icon`}
                    style={{
                      height: 100,
                      width: 'auto',
                      marginTop: 20,
                    }}
                  />
                </Space>
              }
            >
              <Space size={[1, 1]} wrap style={{ marginBottom: 18 }}>
                {command.osep_generator_ui_maintained_by && (
                  <Tooltip
                    title={
                      'Maintained by ' + command.osep_generator_ui_maintained_by
                    }
                  >
                    <Tag
                      color={StrykerPerformanceIndicatorColor.GREEN}
                      key="osep"
                    >
                      {command.osep_generator_ui_maintained_by}
                    </Tag>
                  </Tooltip>
                )}
                {command.osep_generator_ui_is_beta && (
                  <Tag color={StrykerPrimaryColor.GRAY} key="beta">
                    Beta
                  </Tag>
                )}
              </Space>
              <Card.Meta
                title={command.osep_generator_ui_short_help}
                description={
                  <InlineMarkdown>
                    {command.osep_generator_ui_help}
                  </InlineMarkdown>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>
    </Page>
  );
};

export default AddProjectsPage;
